import { FunctionComponent, Fragment } from 'preact';
import { useCallback } from 'preact/hooks';

import { usePlayer } from '../../hooks/PlayerProvider';
import { useAdProgress } from '../../hooks/useAdProgress';
import { useTracker } from '../../hooks/useTracker';
import { formatShortDuration } from '../../../../utils/datetime';
import { AdLink } from '../AdLink/AdLink';
import ns from '../../translations';
import { useTranslation } from '../../../../utils/useTranslation';
import { AdData } from '../../hooks/useAdData';
import { useTpaasTracker } from '../../hooks/useTpaasTracker';
import { firePixelTracker } from '../../utils/vast';
import { ButtonProps } from '../Button/Button';

export interface VideoAdLinkProps {
  adData: AdData;
  className?: string;
  size?: ButtonProps['size'];
  variant?: 'default' | 'floating';
}

export const VideoAdLink: FunctionComponent<VideoAdLinkProps> = ({
  adData,
  className,
  size,
  variant = 'default',
}) => {
  const player = usePlayer();
  const { t } = useTranslation(ns);
  const adProgress = useAdProgress();
  const { trackAdLink } = useTracker();
  const { trackEngagementUIElement } = useTpaasTracker();

  const handleAdLinkClick = useCallback(
    (e: MouseEvent) => {
      player.pause();
      adData.clickTrackers.forEach(firePixelTracker);
      trackAdLink(adData);
      if (e.currentTarget instanceof Element) {
        trackEngagementUIElement({
          elementName: 'Ad Link',
          elementText: e.currentTarget.textContent ?? undefined,
          elementType: e.currentTarget.tagName.toLowerCase(),
          name: 'Ad',
        });
      }
    },
    [player, trackAdLink, trackEngagementUIElement, adData]
  );

  const seconds = Math.ceil(adData.meta.duration - adProgress.time);

  const labelMeta = [`${seconds}s`];
  if (adData.meta.podcount > 1) {
    const { sequence, podcount } = adData.meta;
    labelMeta.unshift(`${sequence} av ${podcount} •`);
  }

  if (variant === 'floating') {
    return (
      <AdLink
        label={t('advertisement')}
        text={labelMeta.join(' ')}
        handleAdLinkClick={handleAdLinkClick}
        url={
          adData.meta.clickThroughUrl ||
          adData.meta.ima?.ad?.data?.clickThroughUrl
        }
        className={className}
        size={size}
        fontSize="small"
      />
    );
  }

  return (
    <AdLink
      url={
        adData.meta.clickThroughUrl ||
        adData.meta.ima?.ad?.data?.clickThroughUrl
      }
      label={<Fragment>{t('advertisement')}</Fragment>}
      text={labelMeta.join(' ')}
      handleAdLinkClick={handleAdLinkClick}
    />
  );
};
